import CheckCircleIcon from "@heroicons/react/20/solid/CheckCircleIcon";
import EllipsisHorizontalIcon from "@heroicons/react/20/solid/EllipsisHorizontalIcon";
import XCircleIcon from "@heroicons/react/20/solid/XCircleIcon";
import { useQueryClient } from "@tanstack/react-query";

import {
  adminKeys,
  useGeneratePdfs,
  useGetPdfComponent,
} from "queries/legacy/admin-queries";
import {
  useGetCustomisation,
  useGetProduct,
} from "queries/legacy/product-queries";

import adminPanelStyles from "../AdminPanel.module.scss";

import styles from "./pdfGenerator.module.scss";

export default function PdfGenerator() {
  const queryClient = useQueryClient();

  const { data: product } = useGetProduct();
  const { data: customisation } = useGetCustomisation();

  const {
    data: pdfComponents,
    isLoading: pdfComponentsIsLoading,
    mutate: getPdfUrls,
    reset: resetPdfComponents,
  } = useGeneratePdfs();

  if (!customisation || !product) {
    return null;
  }

  return (
    <div className={[adminPanelStyles.tool, styles.container].join(" ")}>
      {!pdfComponents && (
        <>
          <p className={styles.title}>
            <span className={adminPanelStyles.toolTitle}>Generate PDFs</span>
            <span className={adminPanelStyles.toolDescription}>
              May take a few minutes
            </span>
          </p>
          <button
            className={styles.generateButton}
            disabled={pdfComponentsIsLoading}
            onClick={() => getPdfUrls()}
          >
            {pdfComponentsIsLoading ? "Loading..." : "Generate"}
          </button>
        </>
      )}

      {pdfComponents && (
        <>
          <div className={styles.components}>
            {pdfComponents.components.map((result) => (
              <PdfEntry key={result.component} {...result} />
            ))}
          </div>
          <button
            className={styles.resetButton}
            onClick={() => {
              // Clear data associated with the generated PDFs
              queryClient.invalidateQueries(
                adminKeys.pdfs(product.museSlug, customisation)
              );
              resetPdfComponents();
            }}
          >
            Reset
          </button>
        </>
      )}
    </div>
  );
}

type PdfEntryProps = { component: string; url: string };

function PdfEntry({ component, url }: PdfEntryProps) {
  const { error, status } = useGetPdfComponent(component, url);

  return (
    <a
      className={[styles.component, styles[status]].join(" ")}
      href={status === "success" ? url : "#"}
      rel="noreferrer"
      target="_blank"
    >
      {error &&
      (!error.response ||
        (error.response?.status && error.response.status !== 404)) ? (
        // Critical error
        <XCircleIcon className={styles.componentErrorIcon} />
      ) : (
        <>
          {/* We poll the entry until it's ready, so errors are expected */}
          {(status === "loading" || status === "error") && (
            <EllipsisHorizontalIcon className={styles.componentLoadingIcon} />
          )}
          {status === "success" && (
            <CheckCircleIcon className={styles.componentCompleteIcon} />
          )}
        </>
      )}
      <p className={styles.componentName}>{component}</p>
    </a>
  );
}
